import clsx from 'clsx'
import type { ReactNode } from 'react'

type ToggleSwitchSize = 'sm' | 'md'

interface ToggleSwitchProps {
  /** Decorative state inside an already interactive control, such as a menu item. */
  presentation?: boolean
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  ariaLabel: string
  className?: string
  size?: ToggleSwitchSize
  /**
   * Optional reinforcement icons rendered inside the thumb. Provided as a
   * checked/unchecked pair so "both or neither" is structural - the thumb
   * shows `icons.checked` when on and `icons.unchecked` when off. Icons are
   * decorative; state is conveyed by `aria-checked` and the thumb position.
   * Pair with `size="md"` so the glyph has room to read.
   */
  icons?: { checked: ReactNode; unchecked: ReactNode }
  /**
   * `status` colors both states: green while on, red while off, for a list
   * where off is the consequential choice (such as detaching a column).
   */
  tone?: 'default' | 'status'
}

const SIZE_STYLES: Record<
  ToggleSwitchSize,
  { track: string; thumb: string; on: string; off: string }
> = {
  sm: {
    track: 'h-4 w-7',
    thumb: 'h-3 w-3',
    on: 'translate-x-3.5',
    off: 'translate-x-0.5',
  },
  md: {
    track: 'h-5 w-[34px]',
    thumb: 'h-4 w-4',
    on: 'translate-x-4',
    off: 'translate-x-0.5',
  },
}

export function ToggleSwitch({
  presentation = false,
  checked,
  onChange,
  disabled = false,
  ariaLabel,
  className,
  size = 'sm',
  icons,
  tone = 'default',
}: ToggleSwitchProps) {
  const sizeStyles = SIZE_STYLES[size]
  const Element = presentation ? 'span' : 'button'

  return (
    <Element
      type={presentation ? undefined : 'button'}
      role={presentation ? undefined : 'switch'}
      aria-hidden={presentation || undefined}
      aria-checked={presentation ? undefined : checked}
      aria-label={presentation ? undefined : ariaLabel}
      disabled={presentation ? undefined : disabled}
      onClick={(event) => {
        if (presentation) return
        event.stopPropagation()
        if (!disabled) onChange(!checked)
      }}
      className={clsx(
        'relative inline-flex shrink-0 rounded-full transition-colors',
        sizeStyles.track,
        disabled
          ? 'bg-[color:var(--border-strong)] opacity-70 disabled:cursor-not-allowed'
          : checked
            ? tone === 'status'
              ? 'bg-[var(--color-success-600)]'
              : 'bg-[color:var(--control-on)]'
            : tone === 'status'
              ? 'bg-[var(--color-danger-500)]'
              : 'bg-[color:var(--border-strong)]',
        className
      )}
    >
      <span
        aria-hidden={icons ? true : undefined}
        className={clsx(
          'absolute top-0.5 flex items-center justify-center rounded-full transition-transform',
          sizeStyles.thumb,
          checked ? sizeStyles.on : sizeStyles.off,
          icons && '[&>svg]:block',
          // eslint-disable-next-line no-restricted-syntax -- the thumb is white in both themes
          'bg-white',
          icons &&
            (disabled
              ? // eslint-disable-next-line no-restricted-syntax -- icons on the white thumb, in both themes
                'text-gray-400'
              : checked
                ? tone === 'status'
                  ? 'text-[var(--color-success-600)]'
                  : 'text-[color:var(--control-on)]'
                : tone === 'status'
                  ? 'text-[var(--color-danger-500)]'
                  : // eslint-disable-next-line no-restricted-syntax -- icons on the white thumb, in both themes
                    'text-gray-600')
        )}
      >
        {icons ? (checked ? icons.checked : icons.unchecked) : null}
      </span>
    </Element>
  )
}
